import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Container,
	Paper,
	Typography,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Snackbar,
	Alert,
	CircularProgress,
} from "@mui/material";
import { ExternalLink, Plus, Edit2, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";
import { ROLES } from "../../config/supabase";

const TABLE_NAME = "portal_links";

const normalizeUrl = (raw) => {
	const trimmed = (raw || "").trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
};

const isValidUrl = (raw) => {
	try {
		const url = new URL(normalizeUrl(raw));
		return url.protocol === "http:" || url.protocol === "https:";
	} catch {
		return false;
	}
};

const Links = () => {
	const { user } = useAuth();
	const [links, setLinks] = useState([]);
	const [loading, setLoading] = useState(true);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedLink, setSelectedLink] = useState(null);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [saving, setSaving] = useState(false);

	const [form, setForm] = useState({
		name: "",
		description: "",
		url: "",
	});

	const isAdmin = user?.profile?.role === ROLES.FUNMIYO_ADMIN;

	const fetchLinks = async () => {
		try {
			const { data, error } = await supabase
				.from(TABLE_NAME)
				.select("*")
				.order("created_at", { ascending: false });

			if (error) throw error;
			setLinks(data || []);
		} catch (err) {
			console.error("Error fetching links:", err);
			setSnackbar({
				open: true,
				message: "Failed to load links",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchLinks();
	}, []);

	const resetForm = () => {
		setForm({ name: "", description: "", url: "" });
		setSelectedLink(null);
	};

	const validateForm = () => {
		if (!form.name.trim()) {
			setSnackbar({ open: true, message: "Name is required", severity: "warning" });
			return false;
		}
		if (!form.url.trim()) {
			setSnackbar({ open: true, message: "URL is required", severity: "warning" });
			return false;
		}
		if (!isValidUrl(form.url)) {
			setSnackbar({
				open: true,
				message: "Enter a valid URL (e.g. https://example.com)",
				severity: "warning",
			});
			return false;
		}
		return true;
	};

	const handleAdd = async () => {
		if (!validateForm()) return;
		setSaving(true);
		try {
			const { error } = await supabase.from(TABLE_NAME).insert({
				name: form.name.trim(),
				description: (form.description || "").trim() || null,
				url: normalizeUrl(form.url),
			});

			if (error) throw error;

			setSnackbar({ open: true, message: "Link added successfully", severity: "success" });
			setAddDialogOpen(false);
			resetForm();
			fetchLinks();
		} catch (err) {
			console.error("Error adding link:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to add link",
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleEdit = async () => {
		if (!selectedLink || !validateForm()) return;
		setSaving(true);
		try {
			const { error } = await supabase
				.from(TABLE_NAME)
				.update({
					name: form.name.trim(),
					description: (form.description || "").trim() || null,
					url: normalizeUrl(form.url),
					updated_at: new Date().toISOString(),
				})
				.eq("id", selectedLink.id);

			if (error) throw error;

			setSnackbar({ open: true, message: "Link updated successfully", severity: "success" });
			setEditDialogOpen(false);
			resetForm();
			fetchLinks();
		} catch (err) {
			console.error("Error updating link:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to update link",
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!selectedLink) return;
		setSaving(true);
		try {
			const { error } = await supabase.from(TABLE_NAME).delete().eq("id", selectedLink.id);
			if (error) throw error;

			setSnackbar({ open: true, message: "Link deleted successfully", severity: "success" });
			setDeleteDialogOpen(false);
			setSelectedLink(null);
			fetchLinks();
		} catch (err) {
			console.error("Error deleting link:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to delete link",
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleOpenLink = (link) => {
		window.open(normalizeUrl(link.url), "_blank", "noopener,noreferrer");
	};

	const openEditDialog = (link) => {
		setSelectedLink(link);
		setForm({
			name: link.name,
			description: link.description || "",
			url: link.url,
		});
		setEditDialogOpen(true);
	};

	const openDeleteDialog = (link) => {
		setSelectedLink(link);
		setDeleteDialogOpen(true);
	};

	const renderFormFields = () => (
		<>
			<TextField
				fullWidth
				label="Name"
				required
				value={form.name}
				onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
				sx={{ mt: 1, mb: 2 }}
			/>
			<TextField
				fullWidth
				label="Short description"
				multiline
				rows={2}
				value={form.description}
				onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
				sx={{ mb: 2 }}
			/>
			<TextField
				fullWidth
				label="URL"
				required
				placeholder="https://example.com"
				value={form.url}
				onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
			/>
		</>
	);

	return (
		<Container maxWidth="lg" sx={{ py: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h5" fontWeight={600}>
					Central Tracker
				</Typography>
				{isAdmin && (
					<Button
						variant="contained"
						startIcon={<Plus size={18} />}
						onClick={() => {
							resetForm();
							setAddDialogOpen(true);
						}}
						sx={{
							backgroundColor: "#6366f1",
							"&:hover": { backgroundColor: "#4f46e5" },
						}}>
						Add Link
					</Button>
				)}
			</Box>

			<Paper sx={{ overflow: "hidden" }}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress />
					</Box>
				) : links.length === 0 ? (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">No links yet.</Typography>
						{isAdmin && (
							<Button
								variant="outlined"
								sx={{ mt: 2 }}
								onClick={() => setAddDialogOpen(true)}>
								Add your first link
							</Button>
						)}
					</Box>
				) : (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: "#f9fafb" }}>
									<TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
									<TableCell sx={{ fontWeight: 600 }} align="right">
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{links.map((link) => (
									<TableRow key={link.id} hover>
										<TableCell>{link.name}</TableCell>
										<TableCell sx={{ maxWidth: 280 }}>
											{link.description || "—"}
										</TableCell>
										<TableCell
											sx={{
												maxWidth: 320,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}>
											{link.url}
										</TableCell>
										<TableCell align="right">
											<IconButton
												size="small"
												onClick={() => handleOpenLink(link)}
												title="Open link"
												sx={{ color: "#6366f1" }}>
												<ExternalLink size={18} />
											</IconButton>
											{isAdmin && (
												<>
													<IconButton
														size="small"
														onClick={() => openEditDialog(link)}
														title="Edit">
														<Edit2 size={18} />
													</IconButton>
													<IconButton
														size="small"
														onClick={() => openDeleteDialog(link)}
														title="Delete"
														sx={{ color: "#dc2626" }}>
														<Trash2 size={18} />
													</IconButton>
												</>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>

			<Dialog
				open={addDialogOpen}
				onClose={() => !saving && setAddDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Add Link</DialogTitle>
				<DialogContent>{renderFormFields()}</DialogContent>
				<DialogActions>
					<Button onClick={() => !saving && setAddDialogOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleAdd} disabled={saving}>
						{saving ? <CircularProgress size={20} /> : "Add"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={editDialogOpen}
				onClose={() => !saving && setEditDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Edit Link</DialogTitle>
				<DialogContent>{renderFormFields()}</DialogContent>
				<DialogActions>
					<Button onClick={() => !saving && setEditDialogOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleEdit} disabled={saving}>
						{saving ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteDialogOpen}
				onClose={() => !saving && setDeleteDialogOpen(false)}>
				<DialogTitle>Delete link</DialogTitle>
				<DialogContent>
					{selectedLink && (
						<Typography>
							Are you sure you want to delete &quot;{selectedLink.name}&quot;? This
							cannot be undone.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => !saving && setDeleteDialogOpen(false)}
						disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleDelete}
						disabled={saving}>
						{saving ? <CircularProgress size={20} /> : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default Links;
